import { Capacitor, registerPlugin } from '@capacitor/core';

// Lazy-register Cashfree native plugin if available
const CashfreePlugin = registerPlugin('CashfreePlugin', {
  web: () => import('capacitor-plugin-cashfree-pg').catch(() => null)
});

/**
 * Cashfree Payment Gateway Native and Web Checkout Wrapper
 */
export const NativeCashfree = {
  /**
   * Check if Cashfree native plugin is available
   */
  isNativeAvailable: () => Capacitor.isNativePlatform() && Boolean(Capacitor.isPluginAvailable('CashfreePlugin')),

  /**
   * Start Checkout using Cashfree
   * @param {Object} params
   * @param {string} params.paymentSessionId - Cashfree order payment session ID from backend
   * @param {string} params.orderId - NEXDESK Order ID
   * @param {'SANDBOX'|'PRODUCTION'} [params.environment='PRODUCTION'] - Environment mode
   * @returns {Promise<{ status: 'SUCCESS'|'FAILED'|'CANCELLED', orderId: string, raw?: any }>}
   */
  checkout: async ({ paymentSessionId, orderId, environment = 'PRODUCTION' }) => {
    if (!paymentSessionId) {
      throw new Error('paymentSessionId is required to start Cashfree checkout');
    }

    if (Capacitor.isNativePlatform()) {
      try {
        const result = await CashfreePlugin.doPayment({
          paymentSessionId,
          orderId,
          environment: environment.toUpperCase()
        });
        return {
          status: result?.status || 'SUCCESS',
          orderId: result?.orderId || orderId,
          raw: result
        };
      } catch (nativeError) {
        console.error('Cashfree Native checkout error:', nativeError);
        throw nativeError;
      }
    }

    // Web Fallback: Load Cashfree JS SDK v3 dynamically if not already loaded
    return new Promise((resolve, reject) => {
      const loadScript = () => {
        if (window.Cashfree) return Promise.resolve(window.Cashfree);
        return new Promise((res, rej) => {
          const script = document.createElement('script');
          script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
          script.onload = () => res(window.Cashfree);
          script.onerror = rej;
          document.head.appendChild(script);
        });
      };

      loadScript()
        .then((CashfreeFactory) => {
          const cashfree = CashfreeFactory({
            mode: environment.toLowerCase() === 'sandbox' ? 'sandbox' : 'production'
          });

          cashfree.checkout({
            paymentSessionId,
            redirectTarget: '_modal' // Opens modal checkout inside web app
          }).then((res) => {
            if (res.error) {
              reject(res.error);
            } else if (res.paymentDetails) {
              resolve({
                status: 'SUCCESS',
                orderId,
                raw: res.paymentDetails
              });
            }
          }).catch(reject);
        })
        .catch((err) => {
          console.error('Failed to load Cashfree web SDK:', err);
          reject(err);
        });
    });
  }
};

export default NativeCashfree;
